<template>
  <div class="vz-individual-order-cargo-dimensions">
    <vz-cargo-general :order-lock="disabled" @set-mode="setMode" />
    <div class="text-subtitle color-dark mt-20 mb-10">
      <span>Максимальные габариты одного места</span>
      <vz-tooltip-info class="vz-individual-order-cargo-tooltip">
        <template #baloon>
          <span>{{ tooltip }}</span>
        </template>
      </vz-tooltip-info>
    </div>
    <vz-cargo-dimension
      :detail="detail"
      :wrapping="wrapping"
      :disabled="disabled"
      :count="count"
      @change-detail-param="onChangeParam"
      @add-pallet="addModsWrapping"
      @set-mode="setMode"
    />
    <vz-cargo-hint />
  </div>
</template>

<script setup lang="ts">
import {useOrderStore} from '~/stores/order';
import {useWrappingStore} from '~/stores/wrapping';
import {modChecker, OrderDimensionDetail, OrderWrappingItem, OrderWrappingModParameters} from '~/types/types';
import {getModCheckFunction} from '~/common/functions';
import VzCargoGeneral from '~/components/personal/order/cargo/parts/vz-cargo-general.vue';
import VzCargoHint from '~/components/personal/order/cargo/parts/vz-cargo-hint.vue';
import VzCargoDimension from '~/components/personal/order/cargo/parts/vz-cargo-dimension.vue';

const emit = defineEmits(['set-mode'])

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})

const orderStore = useOrderStore()
const wrappingStore = useWrappingStore()

const tooltip = 'Для более точного расчёта стоимости перевозки уточните максимальные параметры одного места, ' +
  'если они превышают: длина – 0.2 м, ширина – 0.2 м, высота – 0.2 м и вес – 2.5 кг.'
const modWeightGeneral = ref(false)
const minVolume = 0.01

const detail = computed((): OrderDimensionDetail => {
  return orderStore.orderForm.cargo.dimensions.detail
})

const wrapping = computed((): OrderWrappingItem[] => {
  return orderStore.orderForm.cargo.dimensions.wrapping
})

const count = computed(() => {
  return orderStore.orderForm.cargo.dimensions.general.place
})

const dimensionsMod = computed((): modChecker => {
  const mod: modChecker = {
    weight: () => false,
    weightProperty: '',
    weightMessage: ''
  }

  const weight = wrappingStore.mods.find(item => item.mod.condition.cargo?.dimension?.detail.weight)
  if (weight) {
    mod.weight = (): boolean => {
      const fc = getModCheckFunction(Object.keys(weight.mod.condition.cargo?.dimension?.detail.weight as {'>=' : 1})[0] as OrderWrappingModParameters['conditions'])
      const detail = fc(parseInt(orderStore.orderForm.cargo.dimensions.detail.weight as never as string), Object.values(weight.mod.condition.cargo?.dimension?.detail.weight as {'>=' : 1})[0])
      modWeightGeneral.value = fc(
        parseInt((orderStore.orderForm.cargo.dimensions.general.weight * orderStore.orderForm.cargo.dimensions.general.place) as never as string),
        Object.values(weight.mod.condition.cargo?.dimension?.detail.weight as {'>=' : 1})[0]
      )
      return detail
    }
    mod.weightProperty = weight.key
    mod.weightMessage = weight.mod.message
  }

  return mod
})

function setMode(value: string) {
  if (value === 'pallets') {
    orderStore.orderForm.cargo.pallets.items = [{
      ...detail.value,
      type: 'nonStandard',
      count: orderStore.orderForm.cargo.pallets.items?.[0]?.count || 1,
      key: 0,
      wrapping: []
    }]
  }
  emit('set-mode', value)
}

function validateWeight() {
  const dimensionsModWrapping = orderStore.orderForm.cargo.dimensions.wrapping.find(item => item.code === dimensionsMod.value.weightProperty)
  // Если нет перегруза по местам и в общих габаритах, то снимаем с паллеты
  if (!dimensionsMod.value.weight() && !modWeightGeneral.value && dimensionsModWrapping) {
    orderStore.orderForm.cargo.dimensions.wrapping =
      orderStore.orderForm.cargo.dimensions.wrapping.filter(item => item.code !== dimensionsMod.value.weightProperty)
  }
}

function addModsWrapping() {
  const dimensionsModWrapping = orderStore.orderForm.cargo.dimensions.wrapping.find(item => item.code === dimensionsMod.value.weightProperty)
  if (!dimensionsModWrapping) {
    orderStore.orderForm.cargo.dimensions.wrapping.push({ code: dimensionsMod.value.weightProperty, value: 1 })
    orderStore.getOrderPriceAction()
  }
}

function onChangeParam({ param, value }: {param: keyof OrderDimensionDetail, value: string|number}) {
  orderStore.orderForm.cargo.dimensions.detail[param] = +value
  if (param === 'weight') {
    validateWeight()
  }
  updateGeneralVolume(param === 'weight' ? 'weight' : 'volume')
  orderStore.getOrderPriceAction()
}

function updateGeneralVolume(type: 'weight'|'volume') {
  if (orderStore.orderForm.cargo.dimensions?.general?.place === null || orderStore.orderForm.cargo.dimensions?.general?.place === 1) {
    if (type === 'weight') {
      orderStore.orderForm.cargo.dimensions.general.weight = detail.value.weight
    } else {
      let newVolume = Number((detail.value.length * detail.value.width * detail.value.height).toFixed(2))
      newVolume = (newVolume < minVolume) ? minVolume : newVolume
      orderStore.orderForm.cargo.dimensions.general.volume = newVolume
    }
  }
}
</script>
